import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';

import {
  AppliedAdjustmentsService,
  TbasService,
  NotificationService,
  UtilsService,
  SessionService,
  NumberFormatService
} from '../../../shared/services';

import { ModalService, ConfirmationModalComponent } from '../../../shared/modules/modal';
import { TbaConstants } from '../../../shared/constants/tba';
import {
  ClosableModalWithErrorResponseModel
} from '../../../shared/components/base/closable-modal-with-error-response-model';

import {
  AppliedAdjustment,
  AppliedAdjustmentErrorResponse,
  Message,
  UncontrolledTransaction,
  ApplyAdjustmentModalPayload,
  Currency
} from '../../../shared/models';

@Component({
  selector: 'apply-adjustment-modal',
  templateUrl: './apply-adjustment-modal.component.html',
})
export class ApplyAdjustmentModalComponent
  extends ClosableModalWithErrorResponseModel<AppliedAdjustmentErrorResponse>
  implements OnInit, OnDestroy {

  appliedAdjustment: AppliedAdjustment;
  transactions: UncontrolledTransaction[] = [];
  data: ApplyAdjustmentModalPayload;
  adjustmentToParameterInputTouched = false;
  upeCurrency: Currency;
  upeCurrencyScale: number;

  inputsOldValue = {
    adjustmentToParameter: '',
  };

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _appliedAdjustmentsService: AppliedAdjustmentsService,
    private _tbasService: TbasService,
    private _utilsService: UtilsService,
    private _sessionService: SessionService,
    private _numberFormatService: NumberFormatService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new AppliedAdjustmentErrorResponse();
  }

  ngOnInit(): void {
    this.upeCurrency = this._sessionService.getContainerUpeCurrency();
    this.upeCurrencyScale = this._sessionService.getContainerUpeCurrencyScale();

    this.initForm();
    this.getTbaTransactions();

    this.listenForErrorMessages();

    if (this.data.appliedAdjustmentId) {
      this.getAppliedAdjustment();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  close(form = null): void {
    if (form && form.form.touched) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Apply Adjustment',
          content: 'Are you sure you want to cancel your actions and close it?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  save(closeModal = true, form = null) {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.responseErrors = [];

    if (this.data.appliedAdjustmentId) {
      this.edit();
    } else {
      this.saveAppliedAdjustment(closeModal, form);
    }
  }

  setOldValue(event, field) {
    this.inputsOldValue[field] = event.target.value;
  }

  setAdjustmentToParameterInputTouchedState(touched = true): void {
    this.adjustmentToParameterInputTouched = touched;
  }

  getFormated(num, maxFractionDigits = 1): string {
    return this._numberFormatService.getFormatedNumber(num, maxFractionDigits);
  }

  shouldShowAdjustmentToParameterErrorMessage(): boolean {
    return !this.appliedAdjustment.adjustmentToParameter && this.adjustmentToParameterInputTouched ||
        (!!this.validationErrors.adjustmentToParameter && !this.adjustmentToParameterInputTouched);
  }

  formatNumberInput(event, element, field): void {
    const validInputRegex = /^[-]?[0-9,]*[.]?[0-9]*$/g;
    const firstChar = event.target.value.slice(0, 1);
    const startsWithDotOrComma = firstChar.includes('.') || firstChar.includes(',');
    const startsWithMinus = firstChar === '-';
    const twoDecimalPrices = { [TbaConstants.TBA_PARAMETERS_COGS_RELATED]: true, [TbaConstants.TBA_PARAMETERS_COGS_UNRELATED]: true, [TbaConstants.TBA_PARAMETERS_RESALE_PRICE]: true, [TbaConstants.TBA_PARAMETERS_SALES_PRICE]: true };
    const maxFractionDigits = twoDecimalPrices[this.data.adjustmentType.tbaParameter.description] ? 2 : 4;

    if (!validInputRegex.test(event.target.value)) {
      event.target.value = this.inputsOldValue[field];

      return;
    }

    this.appliedAdjustment[field] = this._numberFormatService.handleCursorPosition(
      event,
      element,
      this.appliedAdjustment[field],
      maxFractionDigits,
      startsWithDotOrComma,
      startsWithMinus
    );
  }

  getParamAdditionalLabel(): string {
    if (this.data.isAmount) {
      return `(${this.upeCurrency.isoCode})${this.getCurrencyScaleString()}`;
    }

    return '';
  }

  private getCurrencyScaleString(): string {
    return this._utilsService.getCurrencyScaleString(this.upeCurrencyScale);
  }

  private saveAppliedAdjustment(closeModal = true, form = null): void {
    this._appliedAdjustmentsService.save(this.appliedAdjustment).subscribe(() => {
      if (closeModal) {
        this.closeAllModals();
        this.success();

        return;
      }

      this.isSaveDisabled = false;
      this.success();
      this.initForm(form);
    });
  }

  private edit(): void {
    this.responseErrors = [];

    this._appliedAdjustmentsService.edit(this.appliedAdjustment.appliedAdjustmentId, this.appliedAdjustment)
      .subscribe(() => {
        this.closeAllModals();
        this.success();
      });
  }

  private initForm(form?: NgForm) {
    this.appliedAdjustment = new AppliedAdjustment();
    this.appliedAdjustment.adjustmentType = this.data.adjustmentType;
    if (form) {
      form.reset();
      this.adjustmentToParameterInputTouched = false;
    }
  }

  private getTbaTransactions(): void {
    this._tbasService.listUcTransactions(this.data.tbaId, `?sort=identifier&order=asc`).subscribe((res) => {
      this.transactions = res.result.uncontrolledTransactions;
    });
  }

  private getAppliedAdjustment(): void {
    this._appliedAdjustmentsService.show(this.data.appliedAdjustmentId).subscribe(({ data }) => {
      this.appliedAdjustment = data;
    });
  }
}
